import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import portalApi from "./portalApi";
import { useBranding } from "./branding";
import { FileText, Receipt, ChevronRight, Clock, CheckCircle2, XCircle, AlertCircle } from "lucide-react";

const cad = (n) => `CA$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fdate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("en-CA", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };

const ESTIMATE_STATUS = {
  Sent: { label: "Awaiting review", color: "bg-amber-100 text-amber-800", icon: Clock },
  Viewed: { label: "Awaiting review", color: "bg-amber-100 text-amber-800", icon: Clock },
  Approved: { label: "Approved", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  Declined: { label: "Declined", color: "bg-slate-100 text-slate-700", icon: XCircle },
  Expired: { label: "Expired", color: "bg-slate-100 text-slate-500", icon: XCircle },
};

const INVOICE_STATUS = {
  Sent: { label: "Sent", color: "bg-sky-100 text-sky-800" },
  Viewed: { label: "Viewed", color: "bg-sky-100 text-sky-800" },
  "Partially Paid": { label: "Partially paid", color: "bg-amber-100 text-amber-800" },
  Paid: { label: "Paid", color: "bg-emerald-100 text-emerald-800" },
  Overdue: { label: "Overdue", color: "bg-red-100 text-red-800" },
};

export default function PortalHome() {
  const { branding } = useBranding();
  const [me, setMe] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: m }, { data: e }, { data: i }] = await Promise.all([
          portalApi.get("/portal/me"),
          portalApi.get("/portal/estimates"),
          portalApi.get("/portal/invoices"),
        ]);
        setMe(m); setEstimates(e); setInvoices(i);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  const pendingEstimates = estimates.filter((e) => ["Sent", "Viewed"].includes(e.status));
  const unpaidInvoices = invoices.filter((i) => i.balance > 0);
  const unpaidTotal = unpaidInvoices.reduce((s, i) => s + Number(i.balance || 0), 0);
  const primary = branding?.primary_color || "#0F4C81";

  return (
    <div className="space-y-8" data-testid="portal-home">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          Hi, {me?.name?.split(" ")[0] || "there"} 👋
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Here's everything from <span className="text-slate-700 font-medium">{branding?.company_name}</span> in one place.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div
          data-testid="portal-pending-estimates"
          className="bg-white border border-slate-200 rounded-lg p-5"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Awaiting your approval</div>
              <div className="mt-2 font-display text-3xl font-bold text-slate-900">{pendingEstimates.length}</div>
              <div className="text-xs text-slate-500 mt-1">estimate{pendingEstimates.length !== 1 ? "s" : ""}</div>
            </div>
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${primary}14` }}>
              <FileText size={20} style={{ color: primary }} />
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="portal-unpaid-summary">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Balance due</div>
              <div className="mt-2 font-display text-3xl font-bold text-slate-900 tabular">{cad(unpaidTotal)}</div>
              <div className="text-xs text-slate-500 mt-1">
                across {unpaidInvoices.length} invoice{unpaidInvoices.length !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${primary}14` }}>
              <Receipt size={20} style={{ color: primary }} />
            </div>
          </div>
        </div>
      </div>

      {/* Estimates */}
      <section>
        <h2 className="text-lg font-display font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <FileText size={18} /> Estimates
        </h2>
        {estimates.length === 0 ? (
          <EmptyCard text="No estimates yet." />
        ) : (
          <ul className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden">
            {estimates.map((e) => {
              const meta = ESTIMATE_STATUS[e.status] || { label: e.status, color: "bg-slate-100 text-slate-700", icon: AlertCircle };
              const Icon = meta.icon;
              return (
                <li key={e.id}>
                  <Link
                    to={`/portal/estimates/${e.id}`}
                    data-testid={`portal-estimate-row-${e.id}`}
                    className="flex items-center justify-between px-4 sm:px-5 py-4 hover:bg-slate-50 transition-colors gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{e.number}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${meta.color}`}>
                          <Icon size={11} /> {meta.label}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        Sent {fdate(e.created_at)} {e.expiration_date && `· expires ${fdate(e.expiration_date)}`}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="tabular font-semibold text-slate-900">{cad(e.total)}</div>
                    </div>
                    <ChevronRight size={18} className="text-slate-300 shrink-0" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Invoices */}
      <section>
        <h2 className="text-lg font-display font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Receipt size={18} /> Invoices
        </h2>
        {invoices.length === 0 ? (
          <EmptyCard text="No invoices yet." />
        ) : (
          <ul className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden">
            {invoices.map((i) => {
              const meta = INVOICE_STATUS[i.status] || { label: i.status, color: "bg-slate-100 text-slate-700" };
              return (
                <li key={i.id}>
                  <Link
                    to={`/portal/invoices/${i.id}`}
                    data-testid={`portal-invoice-row-${i.id}`}
                    className="flex items-center justify-between px-4 sm:px-5 py-4 hover:bg-slate-50 transition-colors gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{i.number}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        Due {fdate(i.due_date)}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="tabular font-semibold text-slate-900">{cad(i.balance)}</div>
                      <div className="text-[11px] text-slate-500">of {cad(i.total)}</div>
                    </div>
                    <ChevronRight size={18} className="text-slate-300 shrink-0" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function EmptyCard({ text }) {
  return (
    <div className="bg-white border border-dashed border-slate-300 rounded-lg p-8 text-center text-sm text-slate-500">
      {text}
    </div>
  );
}
